/**
 * Guidance and Process Maps API Client
 *
 * Enterprise-grade API client with:
 * - Strict typing using DTOs
 * - Proper error handling
 * - Centralized endpoint configuration
 * - Adapter integration
 */

import {
  GuidanceDto,
  ProcessMapDto,
  ApiResponse,
  CreateGuidanceRequest,
  UpdateGuidanceRequest,
  CreateProcessMapRequest,
  UpdateProcessMapRequest
} from './guidanceAndProcessMaps.dto';

/**
 * API Client Configuration
 */
const API_BASE = '/api/compliance/definition/guidance';

/**
 * Get authentication headers
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  };
}

/**
 * Handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `API Error: ${response.status}`);
  }
  return response.json();
}

/**
 * Guidance and Process Maps API Client
 */
export const guidanceAndProcessMapsApi = {
  /**
   * Get all guidance items
   */
  async getGuidance(): Promise<GuidanceDto[]> {
    const response = await fetch(`${API_BASE}`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<GuidanceDto[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new guidance item
   */
  async createGuidance(data: CreateGuidanceRequest): Promise<GuidanceDto> {
    const response = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<GuidanceDto> = await handleResponse(response);
    return result.data;
  },

  /**
   * Update an existing guidance item
   */
  async updateGuidance(id: number, data: UpdateGuidanceRequest): Promise<GuidanceDto> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<GuidanceDto> = await handleResponse(response);
    return result.data;
  },

  /**
   * Delete a guidance item
   */
  async deleteGuidance(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },

  /**
   * Get all process maps
   */
  async getProcessMaps(): Promise<ProcessMapDto[]> {
    const response = await fetch(`${API_BASE}/process-maps`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<ProcessMapDto[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new process map
   */
  async createProcessMap(data: CreateProcessMapRequest): Promise<ProcessMapDto> {
    const response = await fetch(`${API_BASE}/process-maps`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<ProcessMapDto> = await handleResponse(response);
    return result.data;
  },

  /**
   * Update an existing process map
   */
  async updateProcessMap(id: number, data: UpdateProcessMapRequest): Promise<ProcessMapDto> {
    const response = await fetch(`${API_BASE}/process-maps/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<ProcessMapDto> = await handleResponse(response);
    return result.data;
  },

  /**
   * Delete a process map
   */
  async deleteProcessMap(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/process-maps/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },
};